/**
 * Company: SPHMedia
 * Description: [Restaurant card component for displaying restaurant information]
 */

import DummyImage from "@assets/restaurant/dummyImage.png";
import ReactionToolbar from "@components/Blocks/ReactionToolbar/ReactionToolbar";
import { READ_ARTICLE_COPY } from "@config/kakiMakan";
import Rating from "@elements/Rating/Rating";
import { Image } from "@elements/ResponsiveImage/Image";
import Link from "@elements/Typography/Link";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import { RawImageField } from "@typings/Opensearch";
import { ReactionProp, UserToken } from "@typings/Reactions";
import { ReactElement } from "react";

import { useRestaurantReactions } from "./hooks/useRestaurantReactions";

export type RestaurantCardProps = {
  articleId: string;
  title: string;
  address: string;
  articleUrl: string;
  publishedDate: string;
  imageField?: RawImageField;
  imageAlt?: string;
  rating?: number;
  reactions?: ReactionProp[];
  reactionSetId?: string;
  userToken: UserToken;
  expandedId: string | null;
  onExpand: (articleId: string | null) => void;
};

/**
 * Restaurant card component
 * Description: [Restaurant card component for kaki makan landing page]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function RestaurantCard(
  props: RestaurantCardProps,
): ReactElement {
  const {
    articleId,
    title,
    address,
    articleUrl,
    imageField,
    imageAlt,
    rating,
    reactions,
    reactionSetId,
    publishedDate,
    userToken,
    expandedId,
    onExpand,
  } = props;
  const hasRating = rating !== undefined;
  const hasReactions = Array.isArray(reactions) && reactions.length > 0;

  const {
    reactions: updatedReactions,
    userReactions,
    handleReactionClick,
    showReactions,
  } = useRestaurantReactions({
    currentReactions: reactions || [],
    articleId,
    publishDate: publishedDate,
    reactionSetId,
    userToken,
    uniqueSectionName: "kaki-makan",
  });

  const alignRatingAndReactions =
    hasRating && hasReactions ? "justify-between" : "justify-start";
  const cardMinHeight =
    hasRating || hasReactions
      ? "md:min-h-[142px] lg:min-h-[172px]"
      : "md:min-h-[110px] lg:min-h-[140px]";

  return (
    <div
      data-testid="restaurant-card"
      className="w-full md:max-w-[358px] lg:max-w-[276px]"
    >
      <div className="flex flex-col gap-2 items-start">
        <Link
          relativePath={false}
          destination={articleUrl}
          testId="restaurant-link"
        >
          <div>
            <Image
              displayWidth={276}
              srcWidth={276}
              srcHeight={184}
              src={imageField?.url || DummyImage}
              alt={imageAlt || title}
              dataTestId="restaurant-image"
            />
          </div>
        </Link>
        <div className={`flex flex-col gap-1 w-full ${cardMinHeight}`}>
          {(hasRating || hasReactions) && (
            <div
              data-testid="restaurant-meta"
              className={`flex items-center ${alignRatingAndReactions}`}
            >
              {hasRating && <Rating rating={rating} />}
              {hasReactions && (
                <div data-testid="article-reactions">
                  <ReactionToolbar
                    articleId={articleId}
                    showReactions={showReactions}
                    reactions={updatedReactions}
                    userReactions={userReactions}
                    reactionsError={null}
                    handleReactionClick={handleReactionClick}
                    variant="small"
                    isExpanded={expandedId === articleId}
                    onExpand={onExpand}
                  />
                </div>
              )}
            </div>
          )}
          <Link
            relativePath={false}
            destination={articleUrl}
            testId="restaurant-title-link"
            className="flex flex-col gap-1 w-full"
          >
            <Heading
              testId="restaurant-title"
              className="text-text-body font-secondary text-[22px] font-semibold leading-[130%]"
            >
              {title}
            </Heading>
            <p
              data-testid="restaurant-address"
              className="text-neutral-700 font-medium text-base leading-[150%]"
            >
              {address}
            </p>
          </Link>
        </div>
        <Link
          relativePath={false}
          destination={articleUrl}
          testId="restaurant-cta-link"
        >
          <button
            data-testid="read-article-button"
            className="border border-solid border-brand-primary min-w-[120px] h-10 text-brand-primary font-regular text-xs leading-[120%]"
          >
            {READ_ARTICLE_COPY}
          </button>
        </Link>
      </div>
    </div>
  );
}
